import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import store from "../../../store/store";
import {
  formatNumber,
  formatToINR,
} from "../../../components/FormateToINR/formatToINR";

export const ScriptName = (record) => {
  const opt = record?.marketName?.endsWith("OPT");
  const name = opt
    ? `${record?.symbolName}  ${
        record?.strikePrice ? record?.strikePrice : ""
      } ${record?.series ? record?.series : ""}`
    : record?.symbolName;

  return name;
};

export const TotalPNL = React.memo(({ data, precomputed, groupId }) => {
  const [total, setTotal] = useState(0);
  const socketUpdate = useSelector(
    (state) => state.positionSocketSlice?.data?.[groupId]
  );

  useEffect(() => {
    if (socketUpdate && socketUpdate.totalPnl !== undefined) {
      setTotal(parseInt(socketUpdate.totalPnl));
    } else if (typeof precomputed === "number") {
      setTotal(parseInt(precomputed));
    }
  }, [socketUpdate, precomputed]);

  useEffect(() => {
    if (groupId || typeof precomputed === "number") return;

    const worker = new Worker(new URL("../totalPNLWorker.js", import.meta.url));
    worker.onmessage = (e) => {
      const { total } = e.data;
      setTotal(parseInt(total));
    };

    const interval = setInterval(() => {
      const prices = store?.getState()?.positionSocketSlice?.data;
      worker.postMessage({ data, prices });
    }, 1000);

    return () => {
      clearInterval(interval);
      worker.terminate();
    };
  }, [data, precomputed, groupId]);

  return (
    <span style={{ color: total > 0 ? "green" : "red" }}>
      {total > 0 ? `+${formatToINR(total)}` : `${formatToINR(total)}`}
    </span>
  );
});

export const TotalOutstanding = React.memo(({ data = [], value, type }) => {
  const { total, netQty, lot, netLot } = useMemo(() => {
    return data.reduce(
      (acc, el) => {
        if (el?.positionType === type) {
          // Total holding (absolute sum)
          acc.total += Math.abs(el?.totalQuantity) || 0;
          // Net difference (signed sum - positive for buy, negative for sell)
          acc.netQty += el?.totalQuantity || 0;
          if (el?.marketName !== "NSE EQU") {
            acc.lot += el?.lot || 0;
            if (el?.totalQuantity > 0) {
              acc.netLot += el?.lot || 0;
            } else {
              acc.netLot -= el?.lot || 0;
            }
          }
        }
        return acc;
      },
      { total: 0, netQty: 0, lot: 0, netLot: 0 }
    );
  }, [data, type]);

  const noLot = value?.radioClient === 1 && data?.[0]?.marketName === "NSE EQU";

  if (total === 0) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
      <span>
        {type === "market" ? "Market " : "Intraday "}
        <span style={{ fontWeight: 500 }}>Holding: {formatToINR(total)}</span>
        {!noLot && lot !== 0 ? ` (${formatNumber(lot)} lot)` : ""}
      </span>
      <span
        style={{
          color: netQty > 0 ? "#52c41a" : netQty < 0 ? "#ff4d4f" : "inherit",
          fontSize: "0.9em",
          fontWeight: 500,
        }}
      >
        Net: {netQty > 0 ? "+" : ""}
        {formatToINR(netQty)}{" "}
        {!noLot && netLot !== 0 ? ` (${formatNumber(netLot)} lot)` : ""}
      </span>
    </div>
  );
});

export const TotalMTM = React.memo(({ data, precomputed }) => {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (typeof precomputed === "number") {
      setValue(parseInt(precomputed));
      return;
    }
    const calculateTotalMTM = () => {
      const totalMTM =
        typeof data !== "number" &&
        data?.reduce((acc, curr) => {
          let price =
            store?.getState()?.positionSocketSlice?.data?.[curr?._id]
              ?.withoutFutBrokeragePNL || 0;
          acc = acc + Number(price);
          return acc;
        }, 0);

      setValue(parseInt(totalMTM));
    };

    const interval = setInterval(calculateTotalMTM, 1000);
    return () => clearInterval(interval);
  }, [data, precomputed]);

  // return value;
  return (
    <span style={{ color: value < 0 ? "red" : "green" }}>
      {formatToINR(value)}
    </span>
  );
});
